import type { CSSProperties } from 'react';
import type { PriceChangeStatus } from '../types/card';

// ═══════════════════════════════════════════════════════════════════════════
//  Card Control Tower — Design System
//
//  색상은 한 방향으로만 흐른다:
//    Tier 1 PRIMITIVE  (raw hex, UI 직접 사용 금지)
//      → Tier 2 SEMANTIC  (resolveTokens — 컴포넌트가 쓰는 유일한 층)
//        → Tier 3 TINT/COMPONENT  (태그·스텝·스텟 등 맥락 전용)
//
//  컴포넌트는 useThemeTokens() 가 반환하는 ThemeTokens 만 사용한다.
//  PALETTE 를 직접 import 하지 않는다. Ant Design 기본 색도 사용하지 않는다.
// ═══════════════════════════════════════════════════════════════════════════

// ─── 기능 상수 ────────────────────────────────────────────────────────────────

export const DROP_STATUSES: PriceChangeStatus[] = ['SLIGHT_DROP', 'MODERATE_DROP', 'SEVERE_DROP'];
export const isDrop = (status: PriceChangeStatus) => DROP_STATUSES.includes(status);
/**
 * 가격 상승 카드인가 — 가격 조회의 '가격 상승' 탭과 PriceHub 변경 표의 '상승' 이 같은 술어를 쓴다(감사 card-controltower#55 ·
 * #121 — 두 화면 건수가 어긋나지 않게).
 */
export const isRise = (status: PriceChangeStatus) => status === 'INCREASED';
export const PAGE_SIZE_OPTIONS = [50, 100, 1000];

// ─── 타이포그래피 ─────────────────────────────────────────────────────────────

export const FONT = {
  family: "'Pretendard', -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', sans-serif",
} as const;

/** 본문·라벨 글자 크기 */
export const FONT_SIZE = {
  // 12 = 낱말 라벨(머리 · 배지 · 시각 · 코드 · 캡션) 하한, 13 = 읽는 글 하한. 11 이하는 쓰지 않는다(디자인 시스템 v2 · 사장 결정 2026-11-04 D3 —
  // 감사 card-controltower#836, 예전 11).
  xs:  12,
  sm:  13,
  md:  14,
  lg:  20,
  xl:  32,
} as const;

/** 아이콘 전용 크기 스케일 (글자 크기와 의미가 달라 분리) */
export const ICON_SIZE = {
  /** 인라인 상태 아이콘 (스텟 칩 ▲▼ 등) */
  xs: 10,
  /** 보조 아이콘 (헤더 해/달, 인라인 표시) */
  sm: 14,
  /** 내비게이션 아이콘 */
  md: 16,
  /** 정렬 박스·원형 배지 지름 (사이드바 아이콘 박스, 스텝 번호) */
  box: 20,
  /** 로고 이모지 */
  lg: 22,
  /** 빈 상태 일러스트 아이콘 (테이블 empty) */
  xl: 32,
  /** 모달 대형 완료 아이콘 */
  xxl: 44,
} as const;

export const FONT_WEIGHT = {
  regular:  400,
  medium:   500,
  semibold: 600,
  bold:     700,
} as const;

export const LINE_HEIGHT = {
  /** 아이콘·단일 줄 (여백 제거) */
  none:    1,
  /** 타이트 — 라벨 2줄 */
  tight:   1.2,
  /** 약간 여유 — 사이드바 메뉴 라벨 */
  snug:    1.3,
  /** 본문 */
  normal:  1.35,
  relaxed: 1.5,
} as const;
// ─── 간격 ────────────────────────────────────────────────────────────────────

export const SPACING = {
  xxs:   4,
  xs:    8,
  sm:   12,
  md:   16,
  lg:   20,
  xl:   24,
  xxl:  32,
  '3xl':40,
} as const;

// ─── 라운드 ───────────────────────────────────────────────────────────────────

export const RADIUS = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
} as const;

/**
 * 표 위 선택 줄의 "전체 선택 / 선택 해제" 같은 작은 텍스트 링크 버튼 스타일(HubChangesTable 등).
 * `AppButton variant="link"`에 얹어 쓴다(패딩·높이를 눌러 인라인 텍스트처럼 보이게).
 */
export const COMPACT_LINK_STYLE: CSSProperties = { padding: 0, fontSize: FONT_SIZE.xs, height: 'auto' };

// ─── 그림자 ───────────────────────────────────────────────────────────────────
// 여러 컴포넌트가 같은 boxShadow 문자열을 하드코딩해서 흩어져 있던 걸 여기로 모은다.

export const SHADOW = {
  /** 상태 바·툴바 카드의 은은한 그림자 (DashboardHeader/DropClassificationPanel 등) */
  card: '0 1px 4px rgba(0,0,0,0.06)',
  /** 화면에 고정되어 떠 있는 요소 (JobProgressBar) */
  raised: '0 2px 8px rgba(0,0,0,0.12)',
  /** 화면 중앙의 패널형 카드 (AuthLayout) */
  panel: '0 4px 24px rgba(0,0,0,0.12)',
} as const;

// ─── 레이아웃 치수 ────────────────────────────────────────────────────────────
// 사이드바 너비·헤더 높이 등 여러 컴포넌트가 공유하는 고정 치수.
// 한 곳에서 바꾸면 사이드바·헤더·콘텐츠 여백·진행바가 함께 따라간다.

export const LAYOUT = {
  /** 사이드바 너비 */
  sidebarWidth: 220,
  /** 대시보드 헤더 높이 */
  headerHeight: 56,
  /** 잡 진행바 높이 (헤더 아래 추가 여백 계산용) */
  jobBarHeight: 44,
  /**
   * 본문 상자(`.ct-page`) 최대 폭 — 넓은 화면에서는 사이드바 오른쪽 영역 가운데에 이 폭으로 멈춘다(사장 결정 2026-11-04 D1, 감사
   * card-controltower#834 · 디자인 시스템 v2 §2-2 page). 1920 이면 본문 1,652 → 1,440, 양옆 106.
   */
  contentMaxWidth: 1440,
  /** 폼 한 장(설정 등) 최대 폭 — page 왼쪽 선에 붙인다(가운데 아님, v2 §2-2 narrow). */
  formMaxWidth: 720,
} as const;

// ─── 끊는 폭 · 배치 (디자인 시스템 v2 §2-1 CT 행, 사장 결정 2026-11-04) ─────────────────────────────────────────

/**
 * 화면 폭 끊는 값 — 미디어 쿼리 · matchMedia 에 쓰는 숫자는 이것뿐(`scripts/check-design.js` K3 가 다른 숫자를 막는다).
 * phone < 600 · tablet 600–1023(사이드바는 서랍) · desktop 1024–1439 · wide ≥ 1440. 1280 · 1920 은 캡처 폭으로만 쓴다(질의 금지).
 * 부품 안의 폭 판단(표 열 세트 · 버튼 쌓기 · 옆 패널)은 화면 폭이 아니라 **본문 폭**(`useContentWidth`)으로 한다 — 본문에 최대 폭이
 * 생겨 "화면 폭 − 사이드바 − 여백" 손계산이 틀린다(예전 값 8벌: 640 · 760 · 999 · 1279 · 1439 · 1600 · 1740).
 */
export const BREAKPOINT = {
  /** 이 폭부터 tablet — 그보다 좁으면 phone(한 열 · 누르는 곳 44 · 카드형 행). */
  tablet:  600,
  /** 이 폭부터 desktop — 사이드바 220 이 붙고 여백 24. 그보다 좁으면 사이드바는 서랍. */
  desktop: 1024,
  /** 이 폭부터 wide — 본문이 최대 폭(1440)에 닿기 시작한다. */
  wide:    1440,
} as const;

/** 캡처 · 점검 폭(질의에 쓰지 않는다) — 코덱 화면 점검 · e2e layout-rules 와 같은 셋. */
export const CHECK_WIDTHS = [390, 1280, 1920] as const;

/**
 * 본문 폭 문턱(템플릿 A 열 수 · v2 §2-1) — 본문 560 ≈ 화면 600, 본문 960 ≈ 화면 1024. 템플릿 B 옆 칸은 본문 1000 · 1400 에서 바뀐다.
 * `src/layout.css` 의 @container 값과 같아야 한다(바꾸면 둘 다).
 */
export const CONTENT_BREAKPOINT = {
  /** 이 본문 폭부터 요약 · 단계 묶음 2열 이상. */
  grid2:      560,
  /** 이 본문 폭부터 묶음이 항목 수만큼 한 줄. */
  gridFull:   960,
  /** 이 본문 폭부터 옆 칸(320)이 큰 칸 옆에 선다 — 그보다 좁으면 아래로. */
  splitSide:  1000,
  /** 이 본문 폭부터 옆 칸이 360. */
  splitWide:  1400,
} as const;

/**
 * 표 열 폭 사다리(디자인 시스템 v2 §2-6 · CT 배치 규칙 §2-5) — 머리 14px 굵게 · 칸 여백 · 정렬 아이콘 포함 하한. 머리가 더 길면 머리 폭까지
 * 넓힌다(머리 한 줄, 줄을 꺾지 않는다). 늘어나는 열은 이름 열 하나(폭 없음, minWidth 만).
 */
export const TABLE_COL = {
  select:    48,
  thumb:     64,
  /** 이름 열 — 폭 없이 minWidth 로만(늘어나는 열은 이것 하나). */
  nameMin:   240,
  code:      140,
  /** 금액 — `네이버 가격(원)` 머리가 들어가는 폭. 값 + 변동률 두 줄이면 moneyTwoLine. */
  money:     128,
  moneyTwoLine: 144,
  count:     80,
  percent:   88,
  status:    112,
  /** MM/DD HH:mm */
  time:      112,
  /** YYYY/MM/DD HH:mm */
  timeLong:  152,
  /** 긴 글(메모 · 변경 내역) — 판단 사유는 두 줄 + 펼침(말줄임 금지). */
  longText:  240,
  /** 처리 버튼 1개 · 2개(본문 < 1440 이면 세로로 쌓고 actionStacked). */
  action1:   104,
  action2:   184,
  actionStacked: 112,
} as const;

/** 표 행 높이(v2 §2-9) — CT 는 comfortable 40, 밀집(매장 가격 요청) 36, 두 줄 칸 48. */
export const TABLE_ROW = { compact: 36, comfortable: 40, twoLine: 48 } as const;

// ─── 컨트롤 사이즈 (버튼 등) ──────────────────────────────────────────────────

/**
 * 표 체크 상자 크기(감사 card-controltower#437, 사장 요청 2026-10-29) — 보이는 상자는 18px 로 맞추고, 누르는 곳은 상자를 감싼
 * label 로 44px(TOUCH_TARGET) 이상. antd 표(rowSelection)의 상자도 buildAntTheme 의 Checkbox 에서 같은 크기.
 */
export const TABLE_CHECKBOX_SIZE = 18;
/** 누르는 곳 최소 크기(WCAG 2.5.5 · 390px 점검 기준). */
export const TOUCH_TARGET = 44;

export const CONTROL_SIZE = {
  sm: { fontSize: 12, paddingX: 8,  height: 26 },
  md: { fontSize: 13, paddingX: 14, height: 32 },
  lg: { fontSize: 14, paddingX: 18, height: 44 },
} as const;

// ═══════════════════════════════════════════════════════════════════════════
//  TIER 1 — PRIMITIVE  (raw values, never used directly)
// ═══════════════════════════════════════════════════════════════════════════

const PRIMITIVE = {
  // ── navy ramp — 다크 배경 레이어의 척추 ──────────────────────────────
  navy900: '#111827', navy800: '#1a2640', navy700: '#1e2f4a',
  navy600: '#2d4060', navy500: '#354d6a', navy400: '#4a6080',
  navy300: '#6b82a0', navy200: '#8ba3be', navy100: '#e8eef5',
  // 다크 보조 텍스트 단계(2026-10-20 대비 감사) — 보조와 흐린 글자가 같은 navy200 이라
  // 단계가 사라져 있었다.
  //   navyTextSecondary '#a9bdd4' → navy800 7.82:1 · navy700 6.99:1
  //   navyTextMuted     '#92a9c3' → navy800 6.23:1 · navy700 5.57:1
  //   navyTextEmpty     '#6b82a0' → navy800 3.82:1 · navy700 3.41:1
  // 흐린 글자는 선택 행 배경(tintInfo dark #1d3a63)에서도 4.5:1 이 필요하다(2026-10-20 PR #37
  // 리뷰 — 예전 navy200 #8ba3be 는 4.40:1 로 살짝 모자랐다). navy200 은 태그 배경용으로 남긴다.
  navyTextSecondary: '#a9bdd4',
  navyTextMuted:     '#92a9c3',
  navyTextEmpty:     '#6b82a0',
  // 비활성 글자(textDisabled) — 예전 navy500 #354d6a 는 비활성 버튼 바탕(bgRaised navy700)에서
  // 1.55:1 로 글자가 묻혔다(2026-10-19 코덱 화면 점검 2회차). 흐린 글자보다 한 단계 아래로.
  //   navyTextDisabled  '#869bb4' → navy800 5.28:1 · navy700 4.72:1
  navyTextDisabled:  '#869bb4',

  // ── navy fill — 호버·선택 등 미세 보정용 ─────────────────────────────
  navyFill700: '#1e3050',  // 테이블 행 호버
  navyFill680: '#244068',  // 선택 행 호버
  navyFill720: '#1a2e50',  // colorFillSecondary
  navyFill760: '#162040',  // colorFillTertiary
  navyFill820: '#131f35',  // blocked 스텝 배경

  // ── slate ramp — 라이트 배경/텍스트 ─────────────────────────────────
  slate50:  '#f8fafc', slate100: '#f1f3f6', slate200: '#e2e8f0',
  slate300: '#cbd5e1', slate400: '#94a3b8', slate500: '#6b7280',
  slate700: '#334155', slate900: '#1e293b',

  // ── light 보조 회색 ─────────────────────────────────────────────────
  grayBorderLight: '#e9ecef', grayBorderLight2: '#e5e7eb', grayBorder3: '#d1d5db',
  grayText:        '#6b7280', grayText2: '#9ca3af', grayTextDim: '#64748b',
  grayPlaceholder: '#e9ecef',

  // ── 접근성 보정 텍스트 (2026-10-20 감사 "화면 · 대비") ───────────────
  // 예전엔 보조(textSecondary)와 흐린 글자(textMuted)가 같은 값이었고, 빈 셀 "-"는
  // #c0c4cc(흰 배경 1.75:1)라 거의 보이지 않았다. 세 단계를 유지한 채 올린다.
  //   grayTextSecondary #475569 → 흰 배경 7.58:1 · 페이지 6.82:1
  //   grayTextMuted     #5f6b7a → 흰 배경 5.42:1 · raised 5.18:1 · 페이지 4.88:1
  //                                · 선택 행(tintInfo) 4.61:1 · 호버 행 4.93:1
  //   grayTextEmpty     #858c98 → 흰 배경 3.39:1 · raised 3.24:1 · 페이지 3.05:1
  grayTextSecondary: '#475569',
  grayTextMuted:     '#5f6b7a',
  grayTextEmpty:     '#858c98',
  //   grayTextDisabled  #6b7480 → 흰 배경 4.74:1 · raised 4.53:1 (예전 grayEmpty #c0c4cc 는 1.67:1)
  grayTextDisabled:  '#6b7480',
  fillSubtle:      '#f1f3f6', fillFaint: '#f8fafc',
  rowHoverLight:   '#f0f4ff', rowSelectHoverLight: '#c7d9ff',

  // ── accents (light) ─────────────────────────────────────────────────
  blue500:  '#2563eb', blue600: '#1d4ed8', blue300: '#93c5fd',
  green500: '#16a34a', green600: '#15803d', green300: '#86efac',
  amber500: '#d97706', amber600: '#b45309', amber300: '#fcd34d',
  // red500 — 예전 #c0392b 는 tintDanger 위 4.45:1(재고 0 배지, 감사 #518) → 한 단계 진하게.
  red500:   '#b03224', red300:  '#fca5a5', red600: '#991b1b',

  // ── accents (dark, 톤다운 — 네이비와 같은 채도대) ────────────────────
  blueDark:    '#6aa6f5', blueSolidDark: '#2d6fc4',
  // greenSolidDark — 예전 #2e8f5f 는 흰 글자 4.03:1(초록 적용 버튼, 감사 #518) → 5.41:1.
  greenDark:   '#5fd49a', greenSolidDark: '#25784f',
  amberDark:   '#eab33f',
  redDark:     '#f08a8a', redSolidDark:  '#c25050',

  // ── dark 아웃라인 — status 보다 한 톤 어둡게 (쨍함 완화) ─────────────
  lineAccentDark:  '#3f6aa0',
  lineSuccessDark: '#3a8f63',
  lineWarningDark: '#8a6a2e',
  lineDangerDark:  '#7e4048',

  // ── dark tint 배경 (스텟·태그·스텝) ──────────────────────────────────
  tintInfoDark:    '#1d3a63',
  tintDangerDark:  '#4a2530',
  tintSuccessDark: '#17452e',
  tintWarningDark: '#3a2f12',

  // ── light tint 배경 ─────────────────────────────────────────────────
  // tintInfoLight — 예전 #e0eeff 위 accent(blue500) 4.39:1(관리자 · 선택 칩 · 배지, 감사 #518) → #ebf4ff 4.66:1.
  tintInfoLight:    '#ebf4ff',
  tintDangerLight:  '#fee2e2',
  tintSuccessLight: '#dcfce7',
  tintWarningLight: '#fffbeb',

  // ── 태그 라이트 색 (판매상태/Naver/결과요약) ────────────────────────
  tagRedBgL:     '#fee2e2', tagRedTextL:    '#991b1b', tagRedBorderL:    '#fca5a5',
  tagAmberBgL:   '#fffbeb', tagAmberTextL:  '#92400e', tagAmberBorderL:  '#fcd34d',
  // 회색 태그 글자(DS-1 대비 보정, 2026-11-02) — 판매중지 #9ca3af 2.31:1 · 판매종료 #c0c4cc 1.67:1 · 미등록/매장가 #6b7280 4.39:1
  // (주석의 4.8 은 흰 바탕 값이었다). 세 단계(종료 < 중지 < 미등록)는 그대로 두고 모두 4.5:1 이상으로.
  //   tagGrayTextL  #656d7a → #f3f4f6 4.74:1   tagGrayText2L #6b7280 → #f9fafb 4.63:1
  //   tagGrayText3L #566070 → #f3f4f6 5.78:1 · #fafafa 6.09:1
  tagGrayBgL:    '#f3f4f6', tagGrayTextL:   '#656d7a', tagGrayBorderL:   '#e5e7eb',
  tagGrayBg2L:   '#f9fafb', tagGrayText2L:  '#6b7280',
  tagGrayText3L: '#566070', tagGrayBorder2L:'#d1d5db',
  tagGreenBgL:   '#dcfce7', tagGreenTextL:  '#166534', tagGreenBorderL:  '#86efac',
  tagGreenBg2L:  '#f0fdf4',
  tagRedBg2L:    '#fff1f0',
  tagGrayBg3L:   '#fafafa',
  // 승인대기(UNADMISSION) 전용 — 나머지 판매상태들과 같은 바에 나란히 뜨기 때문에
  // red/amber/gray/green과 겹치지 않는 색이 필요해서 추가.
  tagPurpleBgL:  '#f3e8ff', tagPurpleTextL: '#6b21a8', tagPurpleBorderL: '#d8b4fe',

  // ── 레어도 배지 색 (상품 등록 화면 SAR/UR/SR/RR/AR/ACE/C, 2026-08-31 "상품 등록 개선안") ──
  // 등급을 한눈에 구분하려면 판매상태 태그와 겹치지 않는 색 스펙트럼이 필요하다.
  // light 값은 핸드오프 스펙 그대로. 아래 없는 등급(UR/AR/ACE/C)은 기존 amber/purple/red/gray를 재사용한다.
  rarityPinkBgL:   '#fdf2f8', rarityPinkTextL:   '#9d174d', rarityPinkBorderL:   '#fbcfe8',
  rarityBlueBorderL: '#bfdbfe',
  rarityTealBgL:   '#f0fdfa', rarityTealTextL:   '#0f766e', rarityTealBorderL:   '#99f6e4',
  rarityPurpleBgL: '#faf5ff', rarityPurpleBorderL: '#e9d5ff',
  rarityPinkBgD:   '#3a2230', rarityPinkTextD:   '#f0a8c8', rarityPinkBorderD:   '#6e3a52',
  rarityTealTextD: '#5fd4c0', rarityTealBorderD: '#2e7f70',

  // ── 태그 다크 색 ────────────────────────────────────────────────────
  tagRedTextD:    '#f08a8a', tagRedBorderD:   '#7e4048',
  tagAmberTextD:  '#eab33f', tagAmberBorderD: '#7a5e2a',
  // tagGrayText3D — 판매종료(다크, navy800 바탕) 글자. 예전 #4a6080 은 2.35:1 → #869bb4 5.28:1(DS-1 대비 보정).
  tagGrayTextD:   '#8ba3be', tagGrayText2D:   '#6b82a0', tagGrayText3D: '#869bb4',
  tagGreenTextD:  '#5fd49a', tagGreenBorderD: '#3a8f63',
  tagPurpleTextD: '#c084fc', tagPurpleBorderD: '#7c3fa6', tagPurpleBgD: '#3a2f52',

  // ── 워크플로우 스텝 색 ──────────────────────────────────────────────
  stepActiveBgL:  '#fffbe6', stepDoneBgL:   '#f0fdf4', stepBlockedBgL: '#fafafa',
  stepNumIdleBg:  '#e2e8f0', stepNumIdleText: '#64748b',
  stepNumDisBg:   '#d1d5db', stepNumDisText:  '#9ca3af',

  // ── 다크 아이콘(테마 스위치) ────────────────────────────────────────
  iconSunDark:  '#7a8fa8',
  iconMoonDark: '#7eb8f7',

  // ── 하락 행 hover (CSS 변수) ────────────────────────────────────────
  dropRowHoverDark:  '#5a2e3a',
  dropRowHoverLight: '#fecaca',

  // ── 사이드바 (테마 무관 딥네이비) ───────────────────────────────────
  sidebarBg:       '#0f1e3c',
  sidebarActive:   '#2563eb',
  // 사이드바 위 흰색 투명도 단계
  whiteA06: 'rgba(255,255,255,0.06)',
  whiteA07: 'rgba(255,255,255,0.07)',
  whiteA18: 'rgba(255,255,255,0.18)',
  whiteA28: 'rgba(255,255,255,0.28)',
  whiteA32: 'rgba(255,255,255,0.32)',
  whiteA35: 'rgba(255,255,255,0.35)',
  whiteA50: 'rgba(255,255,255,0.50)',
  whiteA55: 'rgba(255,255,255,0.55)',
  whiteA75: 'rgba(255,255,255,0.75)',

  white: '#ffffff',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
//  SIDEBAR — 테마 무관 고정 딥네이비
//
//  사이드바는 다크/라이트와 무관하게 항상 같은 색을 유지하므로
//  resolveTokens 와 별개로 단일 상수로 export 한다.
//  AppSidebar 는 이 SIDEBAR 만 참조한다.
// ═══════════════════════════════════════════════════════════════════════════

export const SIDEBAR = {
  bg:            PRIMITIVE.sidebarBg,
  bgHover:       PRIMITIVE.whiteA06,
  bgActive:      PRIMITIVE.sidebarActive,
  border:        PRIMITIVE.whiteA07,
  logoText:      PRIMITIVE.white,
  logoSub:       PRIMITIVE.whiteA35,
  // 텍스트 단계는 사이드바 배경(#0f1e3c) 기준 대비로 잡는다(2026-10-20 감사 "화면 · 대비").
  //   navText    whiteA75 → 9.75:1 (예전 whiteA55 5.85:1)
  //   navSub     whiteA55 → 5.85:1 (예전 whiteA32 2.86:1 — 11px 설명이 거의 안 보였다)
  //   groupLabel whiteA50 → 5.08:1 (예전 whiteA28 2.52:1)
  //   versionText whiteA50 → 5.08:1 (예전 whiteA18 1.77:1)
  //   선택된 메뉴의 설명(navSubActive)은 파란 배경(white 5.17:1)에서 흰색까지만 올린다.
  navText:       PRIMITIVE.whiteA75,
  navTextActive: PRIMITIVE.white,
  navSub:        PRIMITIVE.whiteA55,
  navSubActive:  PRIMITIVE.white,
  groupLabel:    PRIMITIVE.whiteA50,
  divider:       PRIMITIVE.whiteA07,
  versionText:   PRIMITIVE.whiteA50,
} as const;

// ═══════════════════════════════════════════════════════════════════════════
//  TIER 3 (palette) — TINT / TAG / STEP  (테마쌍으로 정의)
// ═══════════════════════════════════════════════════════════════════════════

export type StepStatus = 'idle' | 'active' | 'done' | 'blocked';
export interface TagTheme { bg: string; text: string; border: string }

const P = PRIMITIVE;

// 태그 — 판매상태 / Naver / 결과요약
const TAG_PALETTE: Record<string, { light: TagTheme; dark: TagTheme }> = {
  saleActive:     { light: { bg: P.tagRedBgL,   text: P.tagRedTextL,   border: P.tagRedBorderL },
                    dark:  { bg: P.tintDangerDark,  text: P.tagRedTextD,   border: P.tagRedBorderD } },
  saleOutOfStock: { light: { bg: P.tagAmberBgL, text: P.tagAmberTextL, border: P.tagAmberBorderL },
                    dark:  { bg: P.tintWarningDark, text: P.tagAmberTextD, border: P.tagAmberBorderD } },
  saleSuspension: { light: { bg: P.tagGrayBgL,  text: P.tagGrayTextL,  border: P.tagGrayBorderL },
                    dark:  { bg: P.navy700, text: P.tagGrayTextD,  border: P.navy600 } },
  saleClose:      { light: { bg: P.tagGrayBg2L, text: P.tagGrayText2L, border: P.tagGrayBorderL },
                    dark:  { bg: P.navy800, text: P.tagGrayText3D, border: P.navy600 } },
  saleUnknown:    { light: { bg: P.tagGrayBgL,  text: P.tagGrayText3L, border: P.tagGrayBorder2L },
                    dark:  { bg: P.navy700, text: P.tagGrayTextD,  border: P.navy600 } },
  saleWait:       { light: { bg: P.tintInfoLight, text: P.blue600, border: P.blue300 },
                    dark:  { bg: P.tintInfoDark,  text: P.blueDark, border: P.blueSolidDark } },
  saleUnadmission:{ light: { bg: P.tagPurpleBgL, text: P.tagPurpleTextL, border: P.tagPurpleBorderL },
                    dark:  { bg: P.tagPurpleBgD, text: P.tagPurpleTextD, border: P.tagPurpleBorderD } },
  saleRejection:  { light: { bg: P.tagRedBg2L,  text: P.tagRedTextL,  border: P.tagRedBorderL },
                    dark:  { bg: P.navy700,     text: P.tagRedTextD,  border: P.tagRedBorderD } },
  naverSuccess:   { light: { bg: P.tagGreenBgL, text: P.tagGreenTextL, border: P.tagGreenBorderL },
                    dark:  { bg: P.tintSuccessDark, text: P.tagGreenTextD, border: P.tagGreenBorderD } },
  naverError:     { light: { bg: P.tagRedBgL,   text: P.tagRedTextL,   border: P.tagRedBorderL },
                    dark:  { bg: P.tintDangerDark,  text: P.tagRedTextD,   border: P.tagRedBorderD } },
  resultIncrease: { light: { bg: P.tagGreenBg2L,text: P.tagGreenTextL, border: P.tagGreenBorderL },
                    dark:  { bg: P.tintSuccessDark, text: P.tagGreenTextD, border: P.tagGreenBorderD } },
  resultDrop:     { light: { bg: P.tagRedBg2L,  text: P.tagRedTextL,   border: P.tagRedBorderL },
                    dark:  { bg: P.tintDangerDark,  text: P.tagRedTextD,   border: P.tagRedBorderD } },
  resultUnchanged:{ light: { bg: P.tagGrayBg3L, text: P.tagGrayText3L, border: P.slate200 },
                    dark:  { bg: P.navy700, text: P.navy200,      border: P.navy600 } },
  // "PriceHub 변경" 종류 배지(2026-10-21) — 상승 초록 · 하락 주황 · 공통가 파랑 · 매장가 회색 · 매장 직접 변경 보라.
  hubRise:        { light: { bg: P.tagGreenBg2L,text: P.tagGreenTextL, border: P.tagGreenBorderL },
                    dark:  { bg: P.tintSuccessDark, text: P.tagGreenTextD, border: P.tagGreenBorderD } },
  hubDrop:        { light: { bg: P.tagAmberBgL, text: P.tagAmberTextL, border: P.tagAmberBorderL },
                    dark:  { bg: P.tintWarningDark, text: P.tagAmberTextD, border: P.tagAmberBorderD } },
  hubCommon:      { light: { bg: P.tintInfoLight, text: P.blue600, border: P.blue300 },
                    dark:  { bg: P.tintInfoDark,  text: P.blueDark, border: P.blueSolidDark } },
  // 매장가 회색은 글자 대비 4.5:1 이상인 tagGrayText3L(#566070 on #f3f4f6 5.78:1)을 쓴다.
  hubStore:       { light: { bg: P.tagGrayBgL,  text: P.tagGrayText3L, border: P.tagGrayBorder2L },
                    dark:  { bg: P.navy700, text: P.tagGrayTextD,  border: P.navy600 } },
  hubNotice:      { light: { bg: P.tagPurpleBgL, text: P.tagPurpleTextL, border: P.tagPurpleBorderL },
                    dark:  { bg: P.tagPurpleBgD, text: P.tagPurpleTextD, border: P.tagPurpleBorderD } },
} as const;

/** 태그 팔레트 키 전부 — 대비 테스트가 빠짐없이 돌게(DS-1). */
export const TAG_PALETTE_KEYS: readonly string[] = Object.keys(TAG_PALETTE);

// 레어도 배지 (상품 등록 화면 — SAR/UR/SR/RR/AR/ACE/C, 2026-08-31)
// TAG_PALETTE와 같은 { light, dark } 테마쌍. 미지의 rarity 문자열(포켓몬은 변형이
// 리터럴로 들어와 값이 열려 있다)은 resolveTokens의 rarity() 접근자가 중립 회색으로 폴백한다.
const RARITY_PALETTE: Record<string, { light: TagTheme; dark: TagTheme }> = {
  SAR: { light: { bg: P.rarityPinkBgL,   text: P.rarityPinkTextL,  border: P.rarityPinkBorderL },
         dark:  { bg: P.rarityPinkBgD,   text: P.rarityPinkTextD,  border: P.rarityPinkBorderD } },
  // ACE(ACE SPEC, 포켓몬) — ACE SPEC 카드의 상징색인 빨강을 재사용(2026-09-01). 이 배지는
  // 상품 등록 표의 레어도 컬럼에만 뜨고 판매상태 바와 같은 화면에 안 나와서 겹칠 일이 없다.
  ACE: { light: { bg: P.tagRedBgL,       text: P.tagRedTextL,      border: P.tagRedBorderL },
         dark:  { bg: P.tintDangerDark,   text: P.tagRedTextD,     border: P.tagRedBorderD } },
  UR:  { light: { bg: P.tagAmberBgL,     text: P.tagAmberTextL,    border: P.tagAmberBorderL },
         dark:  { bg: P.tintWarningDark,  text: P.tagAmberTextD,   border: P.tagAmberBorderD } },
  SR:  { light: { bg: P.rowHoverLight,   text: P.blue600,          border: P.rarityBlueBorderL },
         dark:  { bg: P.tintInfoDark,     text: P.blueDark,        border: P.blueSolidDark } },
  RR:  { light: { bg: P.rarityTealBgL,   text: P.rarityTealTextL,  border: P.rarityTealBorderL },
         dark:  { bg: P.tintSuccessDark,  text: P.rarityTealTextD, border: P.rarityTealBorderD } },
  AR:  { light: { bg: P.rarityPurpleBgL, text: P.tagPurpleTextL,   border: P.rarityPurpleBorderL },
         dark:  { bg: P.tagPurpleBgD,     text: P.tagPurpleTextD,  border: P.tagPurpleBorderD } },
  C:   { light: { bg: P.fillFaint,       text: P.grayText,         border: P.grayBorderLight2 },
         dark:  { bg: P.navy700,          text: P.navy200,         border: P.navy600 } },
} as const;

// 워크플로우 스텝
const STEP_PALETTE: Record<StepStatus, {
  bg: { light: string; dark: string };
  border: { light: string; dark: string };
  numBg: string; numColor: string;
}> = {
  idle:    { bg: { light: P.white,         dark: P.navy800 },   border: { light: P.slate200, dark: P.navy600 },        numBg: P.stepNumIdleBg, numColor: P.stepNumIdleText },
  active:  { bg: { light: P.stepActiveBgL, dark: P.tintWarningDark }, border: { light: P.amber500, dark: P.amberDark }, numBg: P.amber500, numColor: P.white },
  done:    { bg: { light: P.stepDoneBgL,   dark: P.tintSuccessDark }, border: { light: P.green500, dark: P.lineSuccessDark }, numBg: P.green500, numColor: P.white },
  blocked: { bg: { light: P.stepBlockedBgL,dark: P.navyFill820 }, border: { light: P.slate200, dark: P.navy700 },        numBg: P.stepNumDisBg, numColor: P.stepNumDisText },
} as const;

// ═══════════════════════════════════════════════════════════════════════════
//  TIER 2 — SEMANTIC  (테마 인식, 컴포넌트가 사용하는 유일한 층)
// ═══════════════════════════════════════════════════════════════════════════

export interface ThemeTokens {
  // ── 배경 (3 레이어) ─────────────────────────────────────────────
  bgPage:    string;  // Layer 0 — 페이지 배경
  bgSurface: string;  // Layer 1 — 패널·카드·테이블
  bgRaised:  string;  // Layer 2 — 헤더·모달·드롭다운
  bgPlaceholder: string;

  // ── 테두리 ─────────────────────────────────────────────────────
  border:    string;
  borderSubtle: string;

  // ── 텍스트 ─────────────────────────────────────────────────────
  textPrimary:   string;  // 본문·제목
  textSecondary: string;  // 보조 설명
  textMuted:     string;  // 비활성·0건
  textDisabled:  string;
  textEmpty:     string;  // 테이블 빈 셀 "-" 자리 표시자 (3:1 — 읽어야 하는 글자엔 textMuted)

  // ── 액센트 (텍스트·아이콘용) ─────────────────────────────────────
  accent:   string;
  success:  string;
  warning:  string;
  danger:   string;

  // ── 솔리드 (채움 + 흰 텍스트용 버튼 배경) ────────────────────────
  accentSolid:  string;
  successSolid: string;
  dangerSolid:  string;
  /** 경고 채움 + 흰 글자(활성 검토 버튼) — warning(다크 #eab33f)에 흰 글자는 1.91:1 이었다(감사 #518). */
  warningSolid: string;

  // ── 아웃라인 전용 (쨍함 완화한 테두리) ──────────────────────────
  lineAccent:  string;
  lineSuccess: string;
  lineWarning: string;
  lineDanger:  string;

  // ── tint 배경 (Stat·배너 등) ────────────────────────────────────
  tintInfo:    string;
  tintDanger:  string;
  tintSuccess: string;
  tintWarning: string;

  // ── 아이콘 ─────────────────────────────────────────────────────
  iconSun:  string;
  iconMoon: string;

  // ── 고정색 ─────────────────────────────────────────────────────
  white:      string;
  sidebarBg:  string;  // 테마 무관 딥네이비

  // ── 접근자 ─────────────────────────────────────────────────────
  tag:  (key: string) => TagTheme;
  step: (status: StepStatus) => { bg: string; border: string; numBg: string; numColor: string };
  /** 레어도 배지 색(SAR/UR/SR/RR/AR/ACE/C). 대소문자 무관, 미지의 등급은 중립 회색으로 폴백. */
  rarity: (code: string | null | undefined) => TagTheme;
}

export function resolveTokens(isDark: boolean): ThemeTokens {
  const m = isDark ? 'dark' : 'light';

  if (isDark) {
    return {
      bgPage:        P.navy900,
      bgSurface:     P.navy800,
      bgRaised:      P.navy700,
      bgPlaceholder: P.navy500,

      border:        P.navy300,
      borderSubtle:  P.navy500,

      textPrimary:   P.navy100,
      textSecondary: P.navyTextSecondary,
      textMuted:     P.navyTextMuted,
      textDisabled:  P.navyTextDisabled,
      textEmpty:     P.navyTextEmpty,

      accent:   P.blueDark,
      success:  P.greenDark,
      warning:  P.amberDark,
      danger:   P.redDark,

      accentSolid:  P.blueSolidDark,
      successSolid: P.greenSolidDark,
      dangerSolid:  P.redSolidDark,
      warningSolid: P.amber600,

      lineAccent:  P.lineAccentDark,
      lineSuccess: P.lineSuccessDark,
      lineWarning: P.lineWarningDark,
      lineDanger:  P.lineDangerDark,

      tintInfo:    P.tintInfoDark,
      tintDanger:  P.tintDangerDark,
      tintSuccess: P.tintSuccessDark,
      tintWarning: P.tintWarningDark,

      iconSun:  P.iconSunDark,
      iconMoon: P.iconMoonDark,

      white:     P.white,
      sidebarBg: P.sidebarBg,

      tag:  (key) => TAG_PALETTE[key]?.[m] ?? TAG_PALETTE.saleUnknown[m],
      step: (status) => {
        const s = STEP_PALETTE[status];
        return { bg: s.bg[m], border: s.border[m], numBg: s.numBg, numColor: s.numColor };
      },
      rarity: (code) => RARITY_PALETTE[(code ?? '').toUpperCase()]?.[m] ?? TAG_PALETTE.resultUnchanged[m],
    };
  }

  return {
    bgPage:        P.slate100,
    bgSurface:     P.white,
    bgRaised:      P.slate50,
    bgPlaceholder: P.grayPlaceholder,

    border:        P.slate500,
    borderSubtle:  P.grayBorderLight,

    textPrimary:   P.slate900,
    textSecondary: P.grayTextSecondary,
    textMuted:     P.grayTextMuted,
    textDisabled:  P.grayTextDisabled,
    textEmpty:     P.grayTextEmpty,

    accent:   P.blue500,
    // 작은 강조 글자(금액 · 건수 · 상태 문구)로 쓰여 흰 바탕 4.5:1 을 넘는 한 단계 진한 색 — green500 3.30:1 · amber500
    // 3.19:1 은 라이트에서 읽기 어려웠다(2026-10-21 코덱 화면 점검, 감사 card-controltower#63).
    success:  P.green600,
    warning:  P.amber600,
    danger:   P.red500,

    accentSolid:  P.blue500,
    // 흰 글자 4.5:1 — green500 은 3.30:1 이었다(감사 #518).
    successSolid: P.green600,
    dangerSolid:  P.red500,
    warningSolid: P.amber600,

    lineAccent:  P.blue300,
    lineSuccess: P.green300,
    lineWarning: P.amber300,
    lineDanger:  P.red300,

    tintInfo:    P.tintInfoLight,
    tintDanger:  P.tintDangerLight,
    tintSuccess: P.tintSuccessLight,
    tintWarning: P.tintWarningLight,

    iconSun:  P.amber500,
    iconMoon: P.slate500,

    white:     P.white,
    sidebarBg: P.sidebarBg,

    tag:  (key) => TAG_PALETTE[key]?.[m] ?? TAG_PALETTE.saleUnknown[m],
    step: (status) => {
      const s = STEP_PALETTE[status];
      return { bg: s.bg[m], border: s.border[m], numBg: s.numBg, numColor: s.numColor };
    },
    rarity: (code) => RARITY_PALETTE[(code ?? '').toUpperCase()]?.[m] ?? TAG_PALETTE.resultUnchanged[m],
  };
}

/** `#rrggbb` 의 WCAG 상대 휘도. */
function relativeLuminance(hex: string): number {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * 채움 배경(배지 · 칩) 위 글자색 — 흰색과 진한 남색 중 대비가 큰 쪽. 다크 테마의 밝은 강조색(blueDark 등) 위 흰 글자는
 * 2.5:1 로 읽기 어려웠다(2026-10-21 코덱 화면 점검, 감사 card-controltower#63). `#rrggbb` 가 아니면 흰색.
 */
export function textOnFill(bg: string): string {
  if (!/^#[0-9a-f]{6}$/i.test(bg)) return P.white;
  const l = relativeLuminance(bg);
  const onWhite = 1.05 / (l + 0.05);
  const onDark = (l + 0.05) / (relativeLuminance(P.slate900) + 0.05);
  return onWhite >= onDark ? P.white : P.slate900;
}

// ═══════════════════════════════════════════════════════════════════════════
//  CSS 변수 (Ant 바깥 영역 — body 배경, 하락 행 강조)
// ═══════════════════════════════════════════════════════════════════════════

export function buildCssVars(isDark: boolean): Record<string, string> {
  const t = resolveTokens(isDark);
  return {
    '--app-font-family':    FONT.family,
    '--app-bg-page':        t.bgPage,
    '--app-bg-placeholder': t.bgPlaceholder,
    '--app-drop-row-base':  t.tintDanger,
    '--app-drop-row-hover': isDark ? PRIMITIVE.dropRowHoverDark : PRIMITIVE.dropRowHoverLight,
    '--app-drop-left-bar':  t.danger,
    // 전역 키보드 포커스 링(감사 #693, index.css) — accent 글자색(바탕 대비 4.5:1 이상)과 같다.
    '--app-focus-ring':     t.accent,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
//  Ant Design ConfigProvider — 토큰 + 컴포넌트 오버라이드
//
//  PALETTE → resolveTokens 를 단일 출처로 사용한다.
//  Ant 기본 색이 새어나오지 않도록 컴포넌트별 배경·테두리를 명시한다.
// ═══════════════════════════════════════════════════════════════════════════

export function buildAntTheme(isDark: boolean) {
  const t = resolveTokens(isDark);

  const token = {
    fontFamily:           FONT.family,
    borderRadius:         RADIUS.md,
    borderRadiusLG:       RADIUS.lg,
    borderRadiusSM:       RADIUS.sm,

    colorPrimary:         t.accent,
    colorSuccess:         t.success,
    colorWarning:         t.warning,
    colorError:           t.danger,
    colorLink:            t.accent,

    colorBgBase:          t.bgSurface,
    colorBgLayout:        t.bgPage,
    colorBgContainer:     t.bgSurface,
    colorBgElevated:      t.bgRaised,
    colorBgSpotlight:     t.borderSubtle,

    colorTextBase:        t.textPrimary,
    colorText:            t.textPrimary,
    colorTextSecondary:   t.textSecondary,
    colorTextTertiary:    t.textMuted,
    colorTextQuaternary:  t.textDisabled,
    // 자리 표시 글자(placeholder) — 입력한 값(textPrimary) · 보조 글(textMuted)보다 한 단계 옅게, 그래도 4.5:1 은 지키는 가장 옅은
    // 회색(textDisabled). 예전엔 Input · Select · DatePicker 가 textMuted 를 써서 빈칸이 값이 든 것처럼 보였다(감사 #49).
    colorTextPlaceholder: t.textDisabled,

    colorBorder:          t.border,
    colorBorderSecondary: t.borderSubtle,

    colorFillAlter:       t.bgRaised,
    colorFillSecondary:   isDark ? PRIMITIVE.navyFill720 : PRIMITIVE.fillSubtle,
    colorFillTertiary:    isDark ? PRIMITIVE.navyFill760 : PRIMITIVE.fillFaint,
    colorFillQuaternary:  isDark ? PRIMITIVE.navy900 : PRIMITIVE.white,
  };

  const components = {
    Button: {
      // Ant 기본 버튼은 type별로 옅은 box-shadow(primary/default/danger Shadow)를 깐다.
      // 디자인 시스템은 버튼에 그림자를 쓰지 않으므로 전부 제거(버튼 아래 흰 그림자 방지).
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow:  'none',
    },
    Table: {
      headerBg:           t.bgRaised,
      headerColor:        t.textPrimary,
      bodyBg:             t.bgSurface,
      rowHoverBg:         isDark ? PRIMITIVE.navyFill700 : PRIMITIVE.rowHoverLight,
      borderColor:        t.border,
      footerBg:           t.bgSurface,
      rowSelectedBg:      t.tintInfo,
      rowSelectedHoverBg: isDark ? PRIMITIVE.navyFill680 : PRIMITIVE.rowSelectHoverLight,
      cellPaddingBlock:   4,
    },
    Input: {
      colorBgContainer:   t.bgSurface,
      colorBorder:        t.border,
      hoverBorderColor:   t.accent,
      activeBorderColor:  t.accent,
      colorTextPlaceholder: t.textDisabled,
    },
    InputNumber: {
      colorBgContainer:   t.bgSurface,
      colorBorder:        t.border,
      hoverBorderColor:   t.accent,
      activeBorderColor:  t.accent,
    },
    Select: {
      colorBgContainer:   t.bgSurface,
      colorBorder:        t.border,
      optionSelectedBg:   t.tintInfo,
      optionActiveBg:     t.bgRaised,
      colorTextPlaceholder: t.textDisabled,
    },
    DatePicker: {
      colorBgContainer:   t.bgSurface,
      colorBgElevated:    t.bgRaised,
      colorBorder:        t.border,
      hoverBorderColor:   t.accent,
      activeBorderColor:  t.accent,
      colorTextPlaceholder: t.textDisabled,
      cellHoverBg:        t.bgRaised,
      cellActiveWithRangeBg: t.tintInfo,
    },
    Modal: {
      contentBg:          t.bgRaised,
      headerBg:           t.bgRaised,
      footerBg:           t.bgRaised,
      titleColor:         t.textPrimary,
    },
    Drawer: {
      colorBgElevated:    t.bgRaised,
      colorText:          t.textPrimary,
    },
    Popconfirm: {
      colorBgElevated:    t.bgRaised,
    },
    Tooltip: {
      colorBgSpotlight:   isDark ? PRIMITIVE.navy500 : t.textPrimary,
      colorTextLightSolid: t.white,
    },
    // 표 체크 상자를 직접 그린 표(판매가 결정 · 스토어 상품 관리)와 같은 18px(감사 #437).
    Checkbox: {
      controlInteractiveSize: TABLE_CHECKBOX_SIZE,
    },
    Pagination: {
      // 선택된 페이지 번호는 솔리드 배경 + 흰 글자로 고정한다 — accent(파랑) 배경에 흰 글자는
      // 다크에서 2.4:1 밖에 안 나와 번호가 묻혔다(2026-10-20 감사 "화면 · 대비").
      // accentSolid 는 흰 글자 기준 라이트 5.17:1 · 다크 5.03:1.
      itemActiveBg:        t.accentSolid,
      itemActiveColor:     t.white,
      itemActiveColorHover: t.white,
      colorPrimary:        t.accent,
    },
    Switch: {
      // Ant 기본(22×44)은 클릭 대상이 24px 미만이라 데스크톱 최소 24px 로 올린다
      // (WCAG 2.5.8, 2026-10-20 감사 "화면 · 크기"). 예전 헤더의 size="small" 은 28×16 이었다.
      trackHeight:  24,
      handleSize:   20,
      trackMinWidth: 40,
      // 핸들·트랙을 줄였으니 안쪽 여백도 같이 맞춘다(2026-10-20 PR #37 리뷰). Ant 는 기본
      // 핸들(18px) 기준 값(innerMinMargin 9 · innerMaxMargin 24)을 그대로 두기 때문에,
      // "가능/불가" 처럼 글자가 들어가는 스위치에서 글자가 핸들 밑으로 파고들었다.
      // 트랙 여백 2px 기준: innerMinMargin = 핸들/2, innerMaxMargin = 핸들 + 2 + 2×2.
      trackPadding:    2,
      innerMinMargin:  10,
      innerMaxMargin:  26,
      colorPrimary:       t.accentSolid,
      colorPrimaryHover:  t.accent,
    },
    Progress: {
      defaultColor:       t.accent,
      remainingColor:     t.border,
    },
    Tag: {
      defaultBg:          t.bgSurface,
      defaultColor:       t.textSecondary,
    },
    Upload: {
      colorBgContainer:   t.bgSurface,
      colorBorder:        t.border,
    },
    Message: {
      contentBg:          t.bgRaised,
    },
  };

  return { token, components };
}